/**
 * 공통 사이드바 껍데기. #app 바깥(body)에 붙어서, 접고 펴도 #app의 화면·선택·풀이 상태를 다시 그리지 않는다.
 * 기기에는 접힘 여부(1비트)만 localStorage에 저장한다. 이름·학습 데이터는 저장하지 않는다.
 * 실제로 있는 경로만 메뉴로 올린다.
 */
import { h } from './dom.js';

export interface NavItem {
  readonly id: string;
  readonly label: string;
  readonly icon: string;
  readonly current: boolean;
  /** 같은 앱 안 이동. 화면 이동 전 호출자가 미저장 답을 지키는 확인을 거친다 */
  readonly onSelect?: () => void;
  /** 다른 화면(전체 로드)으로 가는 경로 */
  readonly href?: string;
}
export interface ShellConfig {
  readonly name: string;
  readonly roleLabel: string;
  readonly items: readonly NavItem[];
  /** 브랜치 클릭: 현재 역할의 메인으로. 이동이 막히면(미저장 답 확인 취소) false를 돌려준다 */
  readonly onHome: () => void;
  readonly onLogout: () => void;
}

const KEY = 'growing.nav.collapsed';
const MOBILE = '(max-width: 899px)';
let root: HTMLElement | null = null;
let escHandler: ((e: KeyboardEvent) => void) | null = null;

function readCollapsed(): boolean { try { return localStorage.getItem(KEY) === '1'; } catch { return false; } }
function writeCollapsed(v: boolean): void { try { localStorage.setItem(KEY, v ? '1' : '0'); } catch { /* 저장소를 못 쓰면 이번 화면에서만 유지 */ } }

export function hideShell(): void {
  root?.remove(); root = null;
  if (escHandler) { document.removeEventListener('keydown', escHandler); escHandler = null; }
  document.body.classList.remove('has-shell', 'nav-collapsed', 'nav-open', 'nav-ready');
}

export function mountShell(cfg: ShellConfig): void {
  hideShell();
  const mq = window.matchMedia(MOBILE);
  let collapsed = readCollapsed();
  let lastFocus: HTMLElement | null = null;
  const body = document.body;
  body.classList.add('has-shell');

  const toggle = h('button', { type: 'button', class: 'nav-toggle', 'data-testid': 'nav-toggle', 'aria-controls': 'gnav' }) as HTMLButtonElement;
  const brand = h('a', { href: '/', class: 'nav-brand', 'data-testid': 'nav-brand', title: '메인으로', 'aria-label': '그로잉어학원 — 메인으로' },
    h('span', { class: 'nav-g', 'aria-hidden': 'true' }, 'G'), h('span', { class: 'nav-brand-text' }, '그로잉어학원')) as HTMLAnchorElement;
  brand.addEventListener('click', (e) => { if (e.metaKey || e.ctrlKey || e.shiftKey) return; e.preventDefault(); closeMobile(false); cfg.onHome(); });

  const links = cfg.items.map((it) => {
    const inner = [h('span', { class: 'nav-ic', 'aria-hidden': 'true' }, it.icon), h('span', { class: 'nav-label' }, it.label)];
    const attrs: Record<string, unknown> = { class: 'nav-item', 'data-testid': `gnav-${it.id}`, title: it.label, 'aria-label': it.label };
    if (it.current) attrs['aria-current'] = 'page';
    if (it.href !== undefined) return h('a', { ...attrs, href: it.href }, ...inner);
    return h('button', { ...attrs, type: 'button', onclick: () => { closeMobile(false); it.onSelect?.(); } }, ...inner);
  });
  const logout = h('button', { type: 'button', class: 'nav-item nav-logout', 'data-testid': 'nav-logout', title: '로그아웃', 'aria-label': '로그아웃', onclick: () => cfg.onLogout() },
    h('span', { class: 'nav-ic', 'aria-hidden': 'true' }, '⎋'), h('span', { class: 'nav-label' }, '로그아웃'));

  const aside = h('aside', { id: 'gnav', class: 'gnav', 'aria-label': '주 메뉴' },
    h('div', { class: 'nav-head' }, brand),
    h('nav', { class: 'nav-list', 'aria-label': '화면 이동' }, h('p', { class: 'nav-group' }, cfg.roleLabel), ...links),
    h('div', { class: 'nav-foot' }, h('p', { class: 'nav-user', 'data-testid': 'nav-user' }, cfg.name), logout));
  const scrim = h('div', { class: 'nav-scrim', 'data-testid': 'nav-scrim', onclick: () => closeMobile(true) });
  const bar = h('div', { class: 'nav-bar' }, toggle, h('span', { class: 'nav-bar-title' }, '그로잉어학원'));
  root = h('div', { class: 'gshell' }, bar, scrim, aside);
  body.append(root);

  function sync(): void {
    const mobile = mq.matches;
    const open = body.classList.contains('nav-open');
    body.classList.toggle('nav-collapsed', !mobile && collapsed);
    const expanded = mobile ? open : !collapsed;
    toggle.setAttribute('aria-expanded', String(expanded));
    toggle.setAttribute('aria-label', expanded ? '메뉴 접기' : '메뉴 펼치기');
    toggle.title = expanded ? '메뉴 접기' : '메뉴 펼치기';
    toggle.textContent = '☰';
    aside.toggleAttribute('inert', mobile && !open);
  }
  function closeMobile(restore: boolean): void {
    if (!body.classList.contains('nav-open')) return;
    body.classList.remove('nav-open'); sync();
    if (restore) (lastFocus ?? toggle).focus();
  }
  toggle.addEventListener('click', () => {
    if (mq.matches) {
      if (body.classList.contains('nav-open')) closeMobile(true);
      else { lastFocus = toggle; body.classList.add('nav-open'); sync(); (aside.querySelector('a,button') as HTMLElement | null)?.focus(); }
    } else { collapsed = !collapsed; writeCollapsed(collapsed); sync(); }
  });
  escHandler = (e) => { if (e.key === 'Escape') closeMobile(true); };
  document.addEventListener('keydown', escHandler);
  mq.addEventListener('change', () => { body.classList.remove('nav-open'); sync(); });
  sync();
  // 처음 그릴 때는 전환 없이 제자리에 놓는다(본문이 옆에서 밀려 들어오지 않게). 이후 접고 펴는 전환만 움직인다.
  requestAnimationFrame(() => requestAnimationFrame(() => { if (root !== null) body.classList.add('nav-ready'); }));
}
